<script setup lang="ts">
import { onBeforeMount, ref, Ref, computed, watch } from "vue";
import ExportDataTableDO = App.Data.ExportScheduler.ExportDataTableDO;
import { useApiClient } from "@/api/client";
import StoredDocumentDO = App.Data.StoredDocumentDO;
import { minLength, required } from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import DataTableDO = App.Data.Explorer.DataTableDO;

const { t } = useI18n();

const exportDataTable = defineModel<ExportDataTableDO>();
const availableStoredDocuments: Ref<StoredDocumentDO[]> = ref();
const isLoading = ref(false);

const computedAvailableStoredDocuments = computed(() => {
	return availableStoredDocuments.value?.filter(
		(storedDocument) => storedDocument.activeDocument?.data_tables.length > 0
	);
});

const computedSelectedStoredDocument = computed(() => {
	return availableStoredDocuments.value?.find(
		(storedDoucment) =>
			storedDoucment.id === exportDataTable.value.stored_document_id
	);
});

const createDataTableNameFromDefinition = (dataTable: DataTableDO): string => {
	let label = `'${t(`properties.${dataTable.metrics[0].name_clean}.title`)}'`;
	if (dataTable.metrics.length > 1) {
		label += ` and ${dataTable.metrics.length - 1} more`;
	}

	label += `, split up by '${t(
		`properties.${dataTable.dimensions[0].name_clean}.title`
	)}'`;

	if (dataTable.dimensions.length > 1) {
		label += ` and ${dataTable.dimensions.length - 1} more`;
	}

	return label;
};

const getAvailableSavedDocumentsForUser = async () => {
	isLoading.value = true;
	try {
		const response = await useApiClient(
			"documents"
		).getAllStoredDocumentsForUser({
			"include[document.dataTables]": true,
		});
		if (response.isOk()) {
			availableStoredDocuments.value = response.value;
		}
	} finally {
		isLoading.value = false;
	}
};

onBeforeMount(() => {
	getAvailableSavedDocumentsForUser();
});

watch(
	() => exportDataTable.value.stored_document_id,
	() => {
		exportDataTable.value.data_table_index = 0;
	}
);
</script>

<template>
	<div class="data-table-export-config d-flex flex-column ga-2">
		<v-skeleton-loader v-if="isLoading" type="article"></v-skeleton-loader>

		<template v-else>
			<!-- Configuration Section -->
			<v-select
				v-model="exportDataTable.stored_document_id"
				:items="computedAvailableStoredDocuments"
				item-title="title"
				item-value="id"
				label="Data Table"
				variant="filled"
				density="comfortable"
				hide-details="auto"
				:rules="[required(t)]"
				placeholder="Select a saved configuration"
				data-testid="select-saved-document"
			/>

			<v-select
				v-if="
					computedSelectedStoredDocument?.activeDocument?.data_tables.length > 1
				"
				v-model="exportDataTable.data_table_index"
				:items="computedSelectedStoredDocument.activeDocument.data_tables"
				:item-title="(item) => createDataTableNameFromDefinition(item)"
				:item-value="
					(item) =>
						computedSelectedStoredDocument.activeDocument.data_tables.indexOf(
							item
						)
				"
				label="Data Table"
				variant="filled"
				density="comfortable"
				hide-details="auto"
				:rules="[required(t)]"
				placeholder="Select a data table"
			/>

			<!-- Email Configuration Section -->
			<div class="d-flex align-center ga-1 mt-4">
				<v-icon icon="mdi-email-outline" size="x-small" />
				<span class="font-weight-medium text-subtitle-2"
					>Email Configuration</span
				>
			</div>

			<v-text-field
				v-model="exportDataTable.email_title"
				label="Subject"
				variant="filled"
				density="comfortable"
				hide-details="auto"
				:rules="[required(t), minLength(t, 10)]"
				placeholder="Enter subject line for the email"
			/>

			<v-textarea
				label="Body"
				hide-details="auto"
				variant="filled"
				v-model="exportDataTable.email_text"
				:rules="[required(t)]"
			/>
		</template>
	</div>
</template>

<style scoped lang="scss">
.data-table-export-config {
	max-width: 100%;
}
</style>
